/**
 * Gear Rack Search Assist v5
 *
 * One shared search contract for desktop, mobile, predictive search and the
 * full search page:
 * - exact words stay exact
 * - the final word may be a prefix from its first character
 * - every meaningful word must match
 * - autocorrect only changes high-confidence typos, never legitimate prefixes
 */
(() => {
  'use strict';

  const STOP_WORDS = new Set([
    'a', 'an', 'and', 'are', 'as', 'at', 'be', 'by', 'for', 'from', 'in', 'into',
    'is', 'it', 'of', 'on', 'or', 'the', 'to', 'with'
  ]);

  const SINGULARIZATION_EXCEPTIONS = new Set([
    'corps', 'eyeglasses', 'glasses', 'series', 'species', 'sunglasses', 'surplus'
  ]);

  const PHRASE_CORRECTIONS = new Map([
    ['multi cam', 'multicam'],
    ['multi-cam', 'multicam'],
    ['ruck sack', 'rucksack'],
    ['ruck-sack', 'rucksack'],
    ['marinecorp', 'marine corps'],
    ['marine corp', 'marine corps'],
    ['airforce', 'air force'],
    ['air-force', 'air force']
  ]);

  // Only true misspellings belong here. Deliberate word beginnings such as
  // "helme", "surplu", "grenad", "paraclet" and "rucksac" are intentionally
  // NOT corrected because they are valid live prefixes.
  const EXACT_CORRECTIONS = new Map([
    ['moly', 'molle'], ['molie', 'molle'], ['molee', 'molle'], ['mole', 'molle'],
    ['filbee', 'filbe'], ['filbie', 'filbe'],
    ['marpatt', 'marpat'], ['marpt', 'marpat'],
    ['multcam', 'multicam'], ['multicm', 'multicam'], ['mulitcam', 'multicam'],
    ['rucksak', 'rucksack'], ['rucksakc', 'rucksack'], ['rucksakcs', 'rucksacks'],
    ['backpak', 'backpack'], ['backpck', 'backpack'],
    ['puch', 'pouch'], ['puches', 'pouches'], ['pouh', 'pouch'], ['pouchs', 'pouches'],
    ['dufel', 'duffel'], ['dufle', 'duffle'], ['dufflebag', 'duffle bag'],
    ['helmt', 'helmet'], ['helment', 'helmet'], ['helemt', 'helmet'], ['hemet', 'helmet'],
    ['glases', 'glasses'], ['glassses', 'glasses'], ['sunglases', 'sunglasses'], ['eyeglasss', 'eyeglasses'],
    ['jackt', 'jacket'], ['jaket', 'jacket'], ['jacekt', 'jacket'],
    ['trouers', 'trousers'], ['trousrs', 'trousers'], ['trouseres', 'trousers'],
    ['pantss', 'pants'], ['shrit', 'shirt'], ['shirrt', 'shirt'],
    ['booots', 'boots'], ['botts', 'boots'], ['glovs', 'gloves'],
    ['hydrtion', 'hydration'], ['hydraton', 'hydration'],
    ['tactial', 'tactical'], ['tacical', 'tactical'],
    ['militry', 'military'], ['militray', 'military'], ['militaryy', 'military'],
    ['surpluss', 'surplus'], ['surpls', 'surplus'], ['surplsu', 'surplus'],
    ['woodlnd', 'woodland'], ['woodand', 'woodland'],
    ['flektarn', 'flecktarn'], ['flecktran', 'flecktarn'],
    ['paracletee', 'paraclete'],
    ['cryeee', 'crye'], ['cryye', 'crye'],
    ['blackhwak', 'blackhawk'], ['blackhawkk', 'blackhawk'],
    ['sealline', 'sealine'],
    ['caneteen', 'canteen'], ['canteeen', 'canteen'],
    ['grenede', 'grenade'],
    ['aviaton', 'aviation'], ['avaition', 'aviation'],
    ['bivvy', 'bivy'], ['bivie', 'bivy'],
    ['sleepng', 'sleeping'], ['sleping', 'sleeping'],
    ['assualt', 'assault'], ['asault', 'assault'],
    ['rifelman', 'rifleman'], ['riflemen', 'rifleman'],
    ['magzine', 'magazine'],
    ['carier', 'carrier'], ['carrrier', 'carrier'],
    ['deploymnt', 'deployment'], ['deplyment', 'deployment'],
    ['barraks', 'barracks'], ['barracs', 'barracks'],
    ['woobee', 'woobie'], ['wooby', 'woobie'],
    ['ponchoo', 'poncho'],
    ['gortex', 'goretex'], ['gore-tex', 'goretex'],
    ['coyte', 'coyote']
  ]);

  const VOCABULARY = [
    'molle', 'filbe', 'ilbe', 'alice', 'marpat', 'multicam', 'ruck', 'rucksack', 'rucksacks',
    'backpack', 'backpacks', 'pouch', 'pouches', 'duffel', 'duffle', 'helmet', 'helmets',
    'glasses', 'eyeglasses', 'sunglasses', 'jacket', 'jackets', 'parka', 'parkas', 'coat',
    'coats', 'shirt', 'shirts', 'pants', 'trousers', 'boots', 'gloves', 'hydration', 'tactical',
    'military', 'surplus', 'woodland', 'flecktarn', 'paraclete', 'crye', 'blackhawk', 'sealine',
    'canteen', 'grenade', 'aviation', 'coverall', 'coveralls', 'bivy', 'sleeping', 'assault',
    'rifleman', 'magazine', 'carrier', 'deployment', 'barracks', 'medical', 'uniform', 'weather',
    'field', 'flight', 'combat', 'strap', 'straps', 'pack', 'packs', 'case', 'cases', 'bag',
    'bags', 'gear', 'liner', 'poncho', 'kneepad', 'kneepads', 'headgear', 'woobie', 'goretex',
    'coyote', 'foliage', 'cold', 'desert', 'radio', 'first', 'aid', 'system', 'vest', 'cover',
    'covers', 'frame', 'waist', 'belt', 'shoulder', 'sustainment', 'utility', 'frag', 'flashbang',
    'safariland', 'mystery', 'ranch', 'gerber', 'norotos', 'patagonia', 'klymit', 'camelbak'
  ];

  const VOCAB_SET = new Set(VOCABULARY);
  const PROTECTED_TERMS = new Set([
    'ocp', 'ucp', 'dcu', 'abu', 'm81', 'aor1', 'aor2', 'mtp', 'us', 'usa', 'usmc', 'usaf',
    'nwu', 'wwi', 'wwii', 'g4', 'g3', 'l1', 'l2', 'l3', 'l4', 'l5', 'l6', 'l7', 'sflcs',
    'soflcs', 'ecwcs', 'pcu', 'cvc', 'nvg', 'ach', 'ech', 'lwh', 'pasgt', 'ihps', 'ivas',
    'aws', 'tyr', 'filbe', 'ilbe', 'molle', 'alice', 'marpat', 'crye'
  ]);

  const suggestionCache = new Map();
  const productMatchCache = new Map();
  let submitSerial = 0;

  function normalize(value) {
    return String(value || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/&/g, ' and ')
      .replace(/[’']/g, '')
      .replace(/[\/_-]+/g, ' ')
      .replace(/[^a-z0-9+.#\s]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function singularize(value) {
    const term = normalize(value);
    if (SINGULARIZATION_EXCEPTIONS.has(term)) return term;
    if (term.length > 4 && term.endsWith('ies')) return `${term.slice(0, -3)}y`;
    if (term.length > 4 && term.endsWith('sses')) return term.slice(0, -2);
    if (term.length > 3 && /(?:ches|shes|xes)$/.test(term)) return term.slice(0, -2);
    if (term.length > 3 && term.endsWith('s') && !term.endsWith('ss')) return term.slice(0, -1);
    return term;
  }

  function tokens(value) {
    return normalize(value).split(' ').filter(Boolean);
  }

  function queryTerms(query) {
    const parts = tokens(query);
    if (!parts.length) return [];
    return parts.filter((term, index) => {
      const isLast = index === parts.length - 1;
      if (isLast) return true;
      return term.length > 1 && !STOP_WORDS.has(term);
    });
  }

  function tokenForms(term) {
    const normalized = normalize(term);
    const singular = singularize(normalized);
    return singular && singular !== normalized ? [normalized, singular] : [normalized];
  }

  function textWords(text) {
    return normalize(text).split(' ').filter(Boolean);
  }

  function exactTokenMatch(text, term) {
    const wanted = tokenForms(term);
    return textWords(text).some((word) => {
      const forms = tokenForms(word);
      return forms.some((form) => wanted.includes(form));
    });
  }

  function prefixTokenMatch(text, term) {
    const wanted = normalize(term);
    const wantedSingular = singularize(wanted);
    if (!wanted) return false;
    return textWords(text).some((word) => {
      const normalizedWord = normalize(word);
      const singularWord = singularize(normalizedWord);
      return normalizedWord.startsWith(wanted) || singularWord.startsWith(wantedSingular);
    });
  }

  function productSearchTexts(data = {}) {
    const strong = [
      data.title,
      data.type,
      data.vendor,
      data.tags,
      data.text,
      data.variants,
      data.sku,
      data.handle
    ].filter(Boolean).join(' ');
    const broad = `${strong} ${data.body || ''}`.trim();
    return { strong, broad };
  }

  function productMatchesQuery(query, data = {}) {
    const terms = queryTerms(query);
    if (!terms.length) return false;
    const { strong, broad } = productSearchTexts(data);

    return terms.every((term, index) => {
      // Any exact token in normal searchable product data is valid.
      if (exactTokenMatch(broad, term)) return true;

      // Only the final word is treated as the word the customer is still typing.
      // Prefix eligibility deliberately uses strong catalog fields, never body
      // copy, so a single letter cannot qualify a product because of a stray
      // description word.
      const isLast = index === terms.length - 1;
      return isLast && prefixTokenMatch(strong, term);
    });
  }

  // Damerau-Levenshtein with adjacent transpositions treated as one typo.
  function editDistance(a, b, limit = 2) {
    a = normalize(a);
    b = normalize(b);
    if (a === b) return 0;
    if (Math.abs(a.length - b.length) > limit) return limit + 1;

    const rows = a.length + 1;
    const cols = b.length + 1;
    const matrix = Array.from({ length: rows }, () => Array(cols).fill(0));
    for (let i = 0; i < rows; i += 1) matrix[i][0] = i;
    for (let j = 0; j < cols; j += 1) matrix[0][j] = j;

    for (let i = 1; i < rows; i += 1) {
      let rowMin = limit + 1;
      for (let j = 1; j < cols; j += 1) {
        const cost = a[i - 1] === b[j - 1] ? 0 : 1;
        let value = Math.min(
          matrix[i - 1][j] + 1,
          matrix[i][j - 1] + 1,
          matrix[i - 1][j - 1] + cost
        );
        if (
          i > 1 && j > 1 &&
          a[i - 1] === b[j - 2] &&
          a[i - 2] === b[j - 1]
        ) {
          value = Math.min(value, matrix[i - 2][j - 2] + 1);
        }
        matrix[i][j] = value;
        rowMin = Math.min(rowMin, value);
      }
      if (rowMin > limit) return limit + 1;
    }
    return matrix[a.length][b.length];
  }

  function allowedTypoDistance(word) {
    const length = normalize(word).length;
    if (length < 4) return 0;
    return length >= 8 ? 2 : 1;
  }

  function explicitCorrection(query) {
    const raw = String(query || '').trim();
    if (!raw) return raw;

    let corrected = raw;
    PHRASE_CORRECTIONS.forEach((replacement, phrase) => {
      const escaped = phrase.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      corrected = corrected.replace(new RegExp(`\\b${escaped}\\b`, 'gi'), replacement);
    });

    corrected = corrected.split(/(\s+)/).map((part) => {
      if (!part || /^\s+$/.test(part)) return part;
      const match = part.match(/^([^A-Za-z0-9]*)([A-Za-z0-9-]+)([^A-Za-z0-9]*)$/);
      if (!match) return part;
      const [, leading, core, trailing] = match;
      const replacement = EXACT_CORRECTIONS.get(normalize(core));
      return replacement ? `${leading}${replacement}${trailing}` : part;
    }).join('');

    return normalize(corrected) === normalize(raw) ? raw : corrected.trim();
  }

  function bestVocabularyCorrection(word) {
    const term = normalize(word);
    if (!term || PROTECTED_TERMS.has(term) || /\d/.test(term) || VOCAB_SET.has(term)) return '';

    const maxDistance = allowedTypoDistance(term);
    if (!maxDistance) return '';

    let best = '';
    let bestDistance = maxDistance + 1;
    let tied = false;

    VOCAB_SET.forEach((candidate) => {
      // A longer word beginning with exactly what the customer typed is a prefix,
      // not a typo. "helme" must stay "helme" and match "helmet".
      if (candidate.startsWith(term)) return;
      if (Math.abs(candidate.length - term.length) > maxDistance) return;
      const distance = editDistance(term, candidate, maxDistance);
      if (distance < bestDistance) {
        best = candidate;
        bestDistance = distance;
        tied = false;
      } else if (distance === bestDistance && distance <= maxDistance && candidate !== best) {
        tied = true;
      }
    });

    return bestDistance <= maxDistance && !tied ? best : '';
  }

  function suggestionLooksLikeCorrection(original, suggestion) {
    const fromTokens = tokens(original);
    const toTokens = tokens(suggestion);
    if (!fromTokens.length || fromTokens.length !== toTokens.length) return false;

    let changed = 0;
    let totalDistance = 0;

    for (let index = 0; index < fromTokens.length; index += 1) {
      const from = fromTokens[index];
      const to = toTokens[index];
      if (from === to) continue;

      if (PROTECTED_TERMS.has(from) || /\d/.test(from)) return false;

      // Never turn an unfinished word into a completed word. Prefix completion
      // belongs to prefix search, not autocorrect.
      if (to.startsWith(from)) return false;

      const maxDistance = allowedTypoDistance(from);
      if (!maxDistance) return false;
      const distance = editDistance(from, to, maxDistance);
      if (distance < 1 || distance > maxDistance) return false;

      changed += 1;
      totalDistance += distance;
    }

    return changed > 0 && changed <= 2 && totalDistance <= 2;
  }

  function predictiveJsonPath() {
    const root = window.Shopify?.routes?.root || '/';
    return `${root}search/suggest.json`;
  }

  function jsonProductData(product) {
    const variants = Array.isArray(product?.variants)
      ? product.variants.map((variant) => `${variant?.title || ''} ${variant?.sku || ''}`).join(' ')
      : '';
    const tags = Array.isArray(product?.tags) ? product.tags.join(' ') : (product?.tags || '');
    return {
      title: product?.title || '',
      type: product?.type || product?.product_type || '',
      vendor: product?.vendor || '',
      tags,
      variants,
      handle: product?.handle || '',
      text: `${product?.title || ''} ${product?.vendor || ''} ${product?.type || product?.product_type || ''} ${variants} ${tags}`
    };
  }

  async function hasStrictPredictiveMatch(query) {
    const key = normalize(query);
    if (!key) return false;
    if (productMatchCache.has(key)) return productMatchCache.get(key);

    const promise = (async () => {
      try {
        const url = new URL(predictiveJsonPath(), window.location.origin);
        url.searchParams.set('q', query);
        url.searchParams.set('resources[type]', 'product');
        url.searchParams.set('resources[limit]', '10');
        url.searchParams.set('resources[limit_scope]', 'each');
        url.searchParams.set('resources[options][fields]', 'title,product_type,variants.title,vendor,tag,variants.sku');
        url.searchParams.set('resources[options][unavailable_products]', 'last');

        const response = await fetch(url.toString(), {
          credentials: 'same-origin',
          headers: { Accept: 'application/json' }
        });
        if (!response.ok) return false;
        const payload = await response.json();
        const products = payload?.resources?.results?.products || [];
        return products.some((product) => productMatchesQuery(query, jsonProductData(product)));
      } catch (error) {
        console.warn('Gear Rack product-prefix lookup failed:', error);
        return false;
      }
    })();

    productMatchCache.set(key, promise);
    return promise;
  }

  async function shopifyQueryCorrection(query) {
    const key = normalize(query);
    if (!key) return '';
    if (suggestionCache.has(key)) return suggestionCache.get(key);

    const promise = (async () => {
      try {
        const url = new URL(predictiveJsonPath(), window.location.origin);
        url.searchParams.set('q', query);
        url.searchParams.set('resources[type]', 'query');
        url.searchParams.set('resources[limit]', '5');
        url.searchParams.set('resources[limit_scope]', 'each');

        const response = await fetch(url.toString(), {
          credentials: 'same-origin',
          headers: { Accept: 'application/json' }
        });
        if (!response.ok) return '';
        const payload = await response.json();
        const suggestions = payload?.resources?.results?.queries || [];
        for (const suggestion of suggestions) {
          const text = String(suggestion?.text || '').trim();
          if (text && suggestionLooksLikeCorrection(query, text)) return text;
        }
      } catch (error) {
        console.warn('Gear Rack query-correction lookup failed:', error);
      }
      return '';
    })();

    suggestionCache.set(key, promise);
    return promise;
  }

  async function resolveCorrection(query, options = {}) {
    const raw = String(query || '').trim();
    if (!raw) return raw;

    const explicit = explicitCorrection(raw);
    if (normalize(explicit) !== normalize(raw)) return explicit;

    const rawTokens = tokens(raw);
    const last = rawTokens[rawTokens.length - 1] || '';
    if (!last || last.length <= 2 || PROTECTED_TERMS.has(last)) return raw;

    if (!options.skipStrictLookup && await hasStrictPredictiveMatch(raw)) return raw;

    const shopifyCorrection = await shopifyQueryCorrection(raw);
    if (shopifyCorrection && normalize(shopifyCorrection) !== normalize(raw)) return shopifyCorrection;

    const parts = raw.split(/(\s+)/);
    let changed = false;
    const correctedParts = parts.map((part, index) => {
      if (!part || /^\s+$/.test(part)) return part;
      const match = part.match(/^([^A-Za-z0-9]*)([A-Za-z0-9-]+)([^A-Za-z0-9]*)$/);
      if (!match) return part;
      const [, leading, core, trailing] = match;

      // Only consider fuzzy correction for actual query words. A prefix candidate
      // is rejected inside bestVocabularyCorrection().
      const replacement = bestVocabularyCorrection(core);
      if (!replacement) return part;
      changed = true;
      return `${leading}${replacement}${trailing}`;
    });

    const local = correctedParts.join('').trim();
    return changed && normalize(local) !== normalize(raw) ? local : raw;
  }

  function searchPath() {
    const root = window.Shopify?.routes?.root;
    return window.theme?.routes?.search_url || (root ? `${root}search` : '/search');
  }

  function searchUrl(query, originalQuery = '') {
    const url = new URL(searchPath(), window.location.origin);
    url.searchParams.set('q', query);
    url.searchParams.set('type', 'product');
    url.searchParams.set('options[unavailable_products]', 'last');
    url.searchParams.set('options[prefix]', 'last');
    if (originalQuery && normalize(originalQuery) !== normalize(query)) {
      url.searchParams.set('gr_original_q', originalQuery);
      url.searchParams.set('gr_autocorrect', '1');
    }
    return url;
  }

  function typedQueryInput(form) {
    if (!(form instanceof HTMLFormElement)) return null;
    return Array.from(form.querySelectorAll('input[name="q"]')).find((input) => {
      const type = String(input.type || '').toLowerCase();
      return type === 'search' || type === 'text' || !type;
    }) || null;
  }

  function isTypedSearchForm(form) {
    if (!(form instanceof HTMLFormElement) || !typedQueryInput(form)) return false;
    try {
      const action = new URL(form.action || searchPath(), window.location.origin);
      const expected = new URL(searchPath(), window.location.origin);
      return action.pathname.replace(/\/$/, '') === expected.pathname.replace(/\/$/, '');
    } catch (_) {
      return false;
    }
  }

  async function interceptSearchSubmit(event) {
    const form = event.target;
    if (!isTypedSearchForm(form)) return;

    const input = typedQueryInput(form);
    const original = input?.value?.trim() || '';
    if (!original) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    const serial = ++submitSerial;

    const corrected = await resolveCorrection(original);
    if (serial !== submitSerial) return;

    const changed = normalize(corrected) !== normalize(original);
    window.location.assign(searchUrl(changed ? corrected : original, changed ? original : '').toString());
  }

  function showCorrectionNotice() {
    const expectedPath = new URL(searchPath(), window.location.origin).pathname.replace(/\/$/, '');
    if (window.location.pathname.replace(/\/$/, '') !== expectedPath) return;

    const params = new URLSearchParams(window.location.search);
    const original = params.get('gr_original_q');
    const corrected = params.get('q');
    if (!original || !corrected || normalize(original) === normalize(corrected)) return;
    if (document.querySelector('.gr-search-correction-notice')) return;

    const target = document.querySelector('[data-gr-search-state]') || document.querySelector('#ProductGridContainer') || document.querySelector('.collection-container');
    if (!target) return;

    const notice = document.createElement('div');
    notice.className = 'gr-search-correction-notice';
    notice.append(document.createTextNode('Showing results for '));

    const strong = document.createElement('strong');
    strong.textContent = corrected;
    notice.append(strong, document.createTextNode(' instead of '));

    const link = document.createElement('a');
    link.href = searchUrl(original).toString();
    link.textContent = original;
    link.setAttribute('aria-label', `Search for ${original} instead`);
    notice.append(link, document.createTextNode('.'));
    target.insertAdjacentElement('beforebegin', notice);
  }

  async function maybeRedirectToCorrection(query) {
    const expectedPath = new URL(searchPath(), window.location.origin).pathname.replace(/\/$/, '');
    if (window.location.pathname.replace(/\/$/, '') !== expectedPath) return false;

    const params = new URLSearchParams(window.location.search);
    if (params.get('gr_autocorrect') === '1') return false;

    const original = String(query || params.get('q') || '').trim();
    if (!original) return false;
    const corrected = await resolveCorrection(original, { skipStrictLookup: true });
    if (!corrected || normalize(corrected) === normalize(original)) return false;

    window.location.replace(searchUrl(corrected, original).toString());
    return true;
  }

  async function autocorrectNativeZeroResultPage() {
    const state = document.querySelector('[data-gr-search-state]');
    if (!state) return;
    const count = Number.parseInt(state.dataset.nativeResultsCount || '', 10);
    if (!Number.isFinite(count) || count !== 0) return;

    const params = new URLSearchParams(window.location.search);
    if (params.get('gr_autocorrect') === '1') return;
    const query = params.get('q') || state.dataset.searchTerms || '';
    if (!query.trim()) return;

    await maybeRedirectToCorrection(query);
  }

  window.GearRackSearchAssist = {
    normalize,
    singularize,
    queryTerms,
    exactTokenMatch,
    prefixTokenMatch,
    productMatchesQuery,
    productSearchTexts,
    explicitCorrection,
    resolveCorrection,
    shopifyQueryCorrection,
    hasStrictPredictiveMatch,
    suggestionLooksLikeCorrection,
    searchUrl,
    editDistance,
    maybeRedirectToCorrection
  };

  document.addEventListener('submit', interceptSearchSubmit, true);

  const init = () => {
    showCorrectionNotice();
    autocorrectNativeZeroResultPage();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();
